/**
 * Tint status SVGs (currentColor) for email preview / Apply.
 * Circle fill is CSS on a 45×45 cell; these are glyph + indicator data URIs.
 */

import {
    BAG_CLASS_TO_TOKEN,
    DECLINED_CLASS_TO_TOKEN,
} from "../constants/statusMaps";
import {
    archiveSvg,
    arrowRightAllSvg,
    arrowUpRightSvg,
    facebookSvg,
    indicatorsSvg,
    indicatorsVSvg,
    instagramSvg,
    invoiceSvg,
    yelpSvg,
    packageSvg,
    truckSvg,
    xSvg,
} from "../figures/markup";
import { declinedSvg } from "../figures/declinedMarkup";
import { emptyBagSvg } from "../figures/emptyBagMarkup";
import { toThemePair } from "./themeTokens";

const HEX_RE = /#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})\b/;

const STATUS_GLYPHS = {
    placed: invoiceSvg,
    ready: packageSvg,
    onRoute: truckSvg,
    completed: archiveSvg,
};

function normalizeHex(input) {
    const raw = String(input || "").trim();
    const m = raw.match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
    if (!m) return null;
    let h = m[1];
    if (h.length === 3) {
        h = h
            .split("")
            .map(c => c + c)
            .join("");
    }
    return `#${h.toLowerCase()}`;
}

function solidHex(cssOrHex, fallback = "#000000") {
    const pair = toThemePair(cssOrHex || fallback);
    if (pair.solid) return pair.solid;
    const m = String(cssOrHex || "").match(HEX_RE);
    return normalizeHex(m?.[0]) || fallback;
}

export function tintSvg(svg, color) {
    const hex = normalizeHex(color) || color;
    return String(svg || "").replace(/currentColor/g, hex);
}

export function svgToDataUri(svg) {
    const markup = String(svg || "").trim();
    const base64 =
        typeof btoa === "function"
            ? btoa(unescape(encodeURIComponent(markup)))
            : Buffer.from(markup, "utf8").toString("base64");
    return `data:image/svg+xml;base64,${base64}`;
}

/** Paint bag layers by class; eyes (unclassed) stay #4D4D4D. */
export function tintBagSvg(svg, tokens) {
    return String(svg || "").replace(
        /class="(path-[^"]+)"([^>]*?)\sfill="[^"]*"/g,
        (full, cls, rest) => {
            const key = BAG_CLASS_TO_TOKEN[cls];
            if (!key) return full;
            const hex = solidHex(tokens[key], null);
            if (!hex) return full;
            return `class="${cls}"${rest} fill="${hex}"`;
        },
    );
}

/** Paint declined illustration layers by class. */
export function tintDeclinedSvg(svg, tokens) {
    return String(svg || "").replace(
        /class="(path-[^"]+)"([^>]*?)\sfill="[^"]*"/g,
        (full, cls, rest) => {
            const key = DECLINED_CLASS_TO_TOKEN[cls];
            if (!key) return full;
            const hex = solidHex(tokens[key], null);
            if (!hex) return full;
            return `class="${cls}"${rest} fill="${hex}"`;
        },
    );
}

/**
 * Tinted SVG markup for each {{THEME.icon*}} / indicator token.
 * Used by preview (data URI) and Apply (PNG → S3).
 * @param {Record<string, string>} tokens already expanded (*Solid pairs)
 * @param {Iterable<string>|Set<string>|null} [onlyKeys] when set, only build listed icon tokens
 * @returns {Record<string, string>}
 */
export function buildTintedIconSvgs(tokens, onlyKeys = null) {
    if (!tokens || typeof tokens !== "object") return {};

    let filter = null;
    if (onlyKeys != null) {
        filter = onlyKeys instanceof Set ? onlyKeys : new Set(onlyKeys);
        // Empty filter → no icons (e.g. contact body with no {{THEME.icon*}}).
        if (filter.size === 0) return {};
    }

    const need = key => !filter || filter.has(key);

    const activeInk = solidHex(tokens.statusIconActive, "#ffffff");
    const inactiveInk = solidHex(tokens.statusIconInactive, "#37404e");
    const dotActive = solidHex(tokens.dotActive, "#6baa33");
    const dotInactive = solidHex(tokens.dotInactive, "#bbc1c9");
    const shopInk = solidHex(tokens.btnShopColor, "#030405");
    const shopInkHover = solidHex(tokens.btnShopColorHover, shopInk);
    const trackInk = solidHex(tokens.btnTrackColor, "#ffffff");
    const trackInkHover = solidHex(tokens.btnTrackColorHover, trackInk);
    const seeAllInk = solidHex(tokens.colorLink, "#2a384e");
    const seeAllInkHover = solidHex(tokens.colorLinkHover, seeAllInk);
    const socialInk = solidHex(tokens.footerIconColor, "#2a384e");

    /** @type {Record<string, string>} */
    const out = {};
    const put = (key, build) => {
        if (need(key)) out[key] = build();
    };

    put("iconPlacedActive", () => tintSvg(STATUS_GLYPHS.placed, activeInk));
    put("iconPlacedInactive", () => tintSvg(STATUS_GLYPHS.placed, inactiveInk));
    put("iconReadyActive", () => tintSvg(STATUS_GLYPHS.ready, activeInk));
    put("iconReadyInactive", () => tintSvg(STATUS_GLYPHS.ready, inactiveInk));
    put("iconOnRouteActive", () => tintSvg(STATUS_GLYPHS.onRoute, activeInk));
    put("iconOnRouteInactive", () =>
        tintSvg(STATUS_GLYPHS.onRoute, inactiveInk),
    );
    put("iconCompletedActive", () =>
        tintSvg(STATUS_GLYPHS.completed, activeInk),
    );
    put("iconCompletedInactive", () =>
        tintSvg(STATUS_GLYPHS.completed, inactiveInk),
    );
    put("indicatorActive", () => tintSvg(indicatorsSvg, dotActive));
    put("indicatorInactive", () => tintSvg(indicatorsSvg, dotInactive));
    put("indicatorActiveV", () => tintSvg(indicatorsVSvg, dotActive));
    put("indicatorInactiveV", () => tintSvg(indicatorsVSvg, dotInactive));
    put("iconBtnShop", () => tintSvg(arrowUpRightSvg, shopInk));
    put("iconBtnShopHover", () => tintSvg(arrowUpRightSvg, shopInkHover));
    put("iconBtnTrack", () => tintSvg(invoiceSvg, trackInk));
    put("iconBtnTrackHover", () => tintSvg(invoiceSvg, trackInkHover));
    put("iconBtnSeeAll", () => tintSvg(arrowRightAllSvg, seeAllInk));
    put("iconBtnSeeAllHover", () => tintSvg(arrowRightAllSvg, seeAllInkHover));
    put("iconBag", () => tintBagSvg(emptyBagSvg, tokens));
    put("iconDeclined", () => tintDeclinedSvg(declinedSvg, tokens));
    put("iconSocialFacebook", () => tintSvg(facebookSvg, socialInk));
    put("iconSocialX", () => tintSvg(xSvg, socialInk));
    put("iconSocialInstagram", () => tintSvg(instagramSvg, socialInk));
    put("iconSocialYelp", () => tintSvg(yelpSvg, socialInk));

    return out;
}

/**
 * Adds {{THEME.icon*}} / {{THEME.indicator*}} data URIs from status + button tokens.
 * @param {Record<string, string>} tokens already expanded (*Solid pairs)
 * @param {Iterable<string>|Set<string>|null} [onlyKeys] limit to tokens used by the template
 */
export function attachStatusFigureTokens(tokens, onlyKeys = null) {
    if (!tokens || typeof tokens !== "object") return tokens || {};

    const svgs = buildTintedIconSvgs(tokens, onlyKeys);
    const iconTokens = {};
    for (const [key, svg] of Object.entries(svgs)) {
        iconTokens[key] = svgToDataUri(svg);
    }
    return {
        ...tokens,
        ...iconTokens,
    };
}
